import type {
  ReportCustomerDto,
  ReportDetailDto,
} from '@act/shared/data-transfer-objects';
import { SECTION_LIBRARY, formatWarehouseMeasurementPeriodLabel } from '@act/shared/data-transfer-objects';
import { useEffect, useMemo } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import {
  normalizeReportCadence,
  toReadingColumnItemWithSnapshot,
  type ReportReadingColumnItem,
} from '../preview/reportPreview.adapter.util';
import { buildReportPreviewSections } from '../utils/reportDisplay.util';
import {
  buildReportReadingRouteHeader,
  type ReportReadingRouteHeader,
} from '../utils/reportReadingRouteHeader.util';
import type { ReportPrintToolbarSection } from '../components/ReportPrintToolbar';

const sectionTitleById = new Map(
  SECTION_LIBRARY.map(section => [section.id, section.title]),
);

export interface UseReportReadingPageDataResult {
  report: ReportDetailDto | null;
  customer: ReportCustomerDto | null;
  reportLoading: boolean;
  customerLoading: boolean;
  reportError: unknown;
  readingItems: ReportReadingColumnItem[];
  toolbarSections: ReportPrintToolbarSection[];
  cadence: ReturnType<typeof normalizeReportCadence>;
  routeHeader: ReportReadingRouteHeader | null;
  measurementPeriod: string;
  isReady: boolean;
}

/**
 * Loads report and customer data and builds reading-column items for view/print routes.
 * @param reportId - Report UUID from the route.
 * @param onlySectionId - When set, filters reading items to a single section.
 * @returns Report reading payload and loading state.
 */
export const useReportReadingPageData = (
  reportId: string,
  onlySectionId = '',
): UseReportReadingPageDataResult => {
  const [report, reportLoading, loadReport, reportError] = useApiCall(
    reportsRequests.getReport,
  );
  const [customer, customerLoading, loadCustomer] = useApiCall(
    reportsRequests.getCustomer,
  );

  useEffect(() => {
    loadReport({ params: { id: reportId } });
  }, [reportId, loadReport]);

  useEffect(() => {
    if (report) {
      loadCustomer({ params: { id: report.customerId } });
    }
  }, [report, loadCustomer]);

  const { readingItems, toolbarSections, cadence, routeHeader } = useMemo(() => {
    if (!report || !customer) {
      return {
        readingItems: [] as ReportReadingColumnItem[],
        toolbarSections: [] as ReportPrintToolbarSection[],
        cadence: normalizeReportCadence(undefined),
        routeHeader: null,
        measurementPeriod: '',
      };
    }

    const measurementPeriod =
      report.measurementPeriod ?? report.reportingPeriod ?? '';

    const previewSections = buildReportPreviewSections(report.sections);
    const items = previewSections
      .map(section =>
        toReadingColumnItemWithSnapshot(
          section,
          customer,
          report.reportingPeriod,
          measurementPeriod,
        ),
      )
      .filter(item => !onlySectionId || item.sectionId === onlySectionId);

    const sections = previewSections.map(section => ({
      sectionId: section.sectionId,
      title: sectionTitleById.get(section.sectionId) ?? section.sectionId,
    }));

    const measurementPeriodLabel = formatWarehouseMeasurementPeriodLabel(
      report.measurementPeriod ?? report.reportingPeriod ?? '',
    );

    return {
      readingItems: items,
      toolbarSections: sections,
      cadence: normalizeReportCadence(report.cadence),
      measurementPeriod,
      routeHeader: buildReportReadingRouteHeader({
        reportName: report.name,
        customerName: customer.name,
        measurementPeriodLabel,
        cadence: normalizeReportCadence(report.cadence),
        includedSectionCount: previewSections.length,
        updatedAt: report.updatedAt,
        lastEditedBy: report.lastEditedBy,
      }),
    };
  }, [report, customer, onlySectionId]);

  const isReady =
    !reportLoading && !customerLoading && Boolean(report && customer);

  const measurementPeriod =
    report?.measurementPeriod ?? report?.reportingPeriod ?? '';

  return {
    report,
    customer,
    reportLoading,
    customerLoading,
    reportError,
    readingItems,
    toolbarSections,
    cadence,
    routeHeader,
    measurementPeriod,
    isReady,
  };
};
